import { useState, type CSSProperties } from "react";
import { actionProps } from "@/lib/controlAnalytics";
import { changeSummary, EXAMPLE_JOB, EXAMPLE_RESUME } from "@/lib/example";
import {
    EXAMPLE_RECORD,
    EXAMPLE_TRACK_COUNTS,
    EXAMPLE_TRACKED,
    TRACK_GROUP,
    type ExampleTrackGroup,
    type ExampleTrackStatus,
    type ExampleTracked,
} from "@/lib/exampleTracking";
import { formatCount } from "@/lib/format";
import ExampleResume from "./ExampleResume";
import JobLine, { CARD } from "./JobLine";
import PlacePreview from "./PlacePreview";
import { CHIP, ListsCard, Record, Track } from "./TrackParts";

const GROUPS: readonly { group: ExampleTrackGroup; label: string }[] = [
    { group: "review", label: "01 · Review" },
    { group: "preparing", label: "02 · Preparing" },
    { group: "history", label: "03 · History" },
];

/** Each status's chip, in History's words and tones. */
const STATUS_CHIP: Record<ExampleTrackStatus, { chip: string; chipClass: string }> = {
    ready: { chip: "Ready", chipClass: "bg-applied text-applied-fg" },
    needs: { chip: "Needs you", chipClass: "bg-queued text-queued-fg" },
    tailoring: { chip: "In progress", chipClass: "bg-card-raised text-fg-muted" },
    sent: { chip: "Sent", chipClass: "bg-applied text-applied-fg" },
    skipped: { chip: "Skipped", chipClass: "bg-skipped text-skipped-fg" },
    closed: { chip: "Closed", chipClass: "bg-skipped text-skipped-fg" },
};

/** The counts over the list, the Overview's own. */
function Counts() {
    return (
        <ul className="m-0 grid list-none grid-cols-5 gap-2 p-0 max-[640px]:grid-cols-3">
            {EXAMPLE_TRACK_COUNTS.map((count, index) => (
                <li
                    key={count.label}
                    className={`flex flex-col gap-0.5 rounded-xl px-3 py-2.5 ${index === 0 ? "bg-applied text-applied-fg" : "bg-card"}`}
                >
                    <span className="text-[12px] font-medium opacity-80">{count.label}</span>
                    <span className="font-mono text-[20px] font-bold leading-none">
                        {formatCount(count.value)}
                    </span>
                </li>
            ))}
        </ul>
    );
}

/** The ready row's extra: who sends, and the tailored resume to open. */
function ReadyExtra({ open }: { open: boolean }) {
    return (
        <div className="flex flex-col gap-3 px-[18px] pb-4">
            <p className="m-0 flex items-center gap-2 text-[13.5px] font-semibold">
                <span className="size-2 shrink-0 rounded-full bg-green" aria-hidden="true" />
                You review and send. The extension fills the form; you press Submit.
            </p>
            {open && (
                <div id="preview-tailored" className="flex flex-col gap-2">
                    <p className="m-0 text-[13px] text-fg-muted">
                        {changeSummary(EXAMPLE_RESUME, EXAMPLE_JOB)}, highlighted.
                    </p>
                    <ExampleResume view="tailored" />
                </div>
            )}
        </div>
    );
}

function Row({
    entry,
    open,
    onToggle,
}: {
    entry: ExampleTracked;
    open: boolean;
    onToggle: () => void;
}) {
    const ready = entry.status === "ready";
    const recorded = entry.job === EXAMPLE_RECORD.job;
    const { chip, chipClass } = STATUS_CHIP[entry.status];
    const quiet = entry.status === "skipped" || entry.status === "closed";
    return (
        <li
            className={`border-t border-line-soft first:border-t-0 ${recorded ? "bg-card-raised" : ""}`}
        >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-[18px] py-3.5">
                <div className={`flex-[1_1_280px] ${quiet ? "opacity-75" : ""}`}>
                    <JobLine job={entry.job} after={entry.line} />
                </div>
                <Track status={entry.status} />
                <span className={`${CHIP} ${chipClass}`}>{chip}</span>
                {ready && (
                    <button
                        {...actionProps("preview_resume_toggled", {
                            source: "ApplicationsPreview",
                            next_state: !open,
                        })}
                        type="button"
                        aria-expanded={open}
                        aria-controls="preview-tailored"
                        onClick={onToggle}
                        className="press inline-flex h-[38px] items-center rounded-[10px] border border-line-control bg-card px-3 text-[13px] font-semibold text-fg transition-[background-color,transform] hover:bg-hover"
                    >
                        {open ? "Hide the tailored resume" : "See the tailored resume"}
                    </button>
                )}
            </div>
            {ready && <ReadyExtra open={open} />}
            {recorded && <Record />}
        </li>
    );
}

/**
 * Applications: the counts, every application grouped as the page groups
 * them, each with its track from found to sent or skipped, the record of
 * one send with the resume that went, and the saved lists they came from.
 */
export default function ApplicationsPreview() {
    const [open, setOpen] = useState(false);
    return (
        <PlacePreview kind="applications">
            <Counts />
            <div className="grid grid-cols-[minmax(0,1fr)_200px] items-start gap-3 max-[860px]:grid-cols-1">
                <div className={`${CARD} overflow-hidden`}>
                    {GROUPS.map(({ group, label }, index) => {
                        const entries = EXAMPLE_TRACKED.filter(
                            (entry) => TRACK_GROUP[entry.status] === group,
                        );
                        if (entries.length === 0) return null;
                        return (
                            <section
                                key={group}
                                aria-label={label}
                                className="flow-step border-t border-line-soft first:border-t-0"
                                style={{ "--step": index } as CSSProperties}
                            >
                                <h2 className="m-0 bg-card-raised px-[18px] py-2 text-[12px] font-semibold text-fg-muted">
                                    {label}
                                </h2>
                                <ul className="m-0 list-none p-0">
                                    {entries.map((entry) => (
                                        <Row
                                            key={entry.job.company}
                                            entry={entry}
                                            open={open}
                                            onToggle={() => setOpen((shown) => !shown)}
                                        />
                                    ))}
                                </ul>
                            </section>
                        );
                    })}
                    <p className="m-0 border-t border-line-soft px-[18px] py-2.5 text-[12px] text-fg-faint">
                        Each track: found or saved, tailored, then sent or skipped.
                    </p>
                </div>
                <ListsCard />
            </div>
        </PlacePreview>
    );
}
